import { useFrame } from '@react-three/fiber'
import type { RefObject } from 'react'
import { useEffect, useRef } from 'react'
import * as THREE from 'three'

import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib'
import type { CameraPresentation, MotionSequence } from '../types'
import { useOrbitControlsAutoCorrection } from './useOrbitControlsAutoCorrection'

const INITIAL_CAMERA_SNAP_FRAMES = 30
const RESTART_CAMERA_TRANSITION_FRAMES = 20
const CAMERA_LERP_ALPHA = 0.05
const RESTART_CAMERA_LERP_ALPHA = 0.16
const ORTHOGRAPHIC_REFERENCE_PAN_Z = 8.5

type Props = {
  controlsRef: RefObject<OrbitControlsImpl | null>
  cameraPresentation?: CameraPresentation
  restartKey?: MotionSequence['restartKey']
}

export const useCamera = ({ controlsRef, cameraPresentation, restartKey }: Props) => {
  const { isAutoCorrectionSuspendedRef } = useOrbitControlsAutoCorrection({ controlsRef, restartKey })
  const previousRestartKeyRef = useRef(restartKey)
  const previousCameraPresentationRef = useRef(cameraPresentation)
  const initialSnapFramesRef = useRef(INITIAL_CAMERA_SNAP_FRAMES)
  const restartTransitionFramesRef = useRef(0)
  const orthographicInitialZoomRef = useRef<number | null>(null)
  const orthographicBaseZoomRef = useRef<number | null>(null)

  useEffect(() => {
    const restartKeyChanged = previousRestartKeyRef.current !== restartKey
    const cameraPresentationChanged = previousCameraPresentationRef.current !== cameraPresentation

    if (restartKeyChanged || cameraPresentationChanged) {
      previousRestartKeyRef.current = restartKey
      previousCameraPresentationRef.current = cameraPresentation
      if (restartKeyChanged) {
        restartTransitionFramesRef.current = RESTART_CAMERA_TRANSITION_FRAMES
        orthographicBaseZoomRef.current = null
      }
    }
  }, [restartKey, cameraPresentation])

  useFrame(({ camera }) => {
    if (!cameraPresentation?.pan && !cameraPresentation?.target) return
    if (isAutoCorrectionSuspendedRef.current) return

    const shouldSnap = initialSnapFramesRef.current > 0
    const isRestartTransition = restartTransitionFramesRef.current > 0
    const lerpAlpha = isRestartTransition ? RESTART_CAMERA_LERP_ALPHA : CAMERA_LERP_ALPHA

    if (cameraPresentation?.pan) {
      if (shouldSnap) {
        camera.position.copy(cameraPresentation.pan)
      } else {
        camera.position.lerp(cameraPresentation.pan, lerpAlpha)
      }

      if (camera instanceof THREE.OrthographicCamera && Number.isFinite(cameraPresentation.pan.z)) {
        if (orthographicInitialZoomRef.current == null) {
          orthographicInitialZoomRef.current = camera.zoom
        }

        if (orthographicBaseZoomRef.current == null) {
          orthographicBaseZoomRef.current = orthographicInitialZoomRef.current
        }

        const safePanZ = Math.max(cameraPresentation.pan.z, 0.001)
        const nextZoom = (orthographicBaseZoomRef.current * ORTHOGRAPHIC_REFERENCE_PAN_Z) / safePanZ
        if (shouldSnap) {
          camera.zoom = nextZoom
        } else {
          camera.zoom = THREE.MathUtils.lerp(camera.zoom, nextZoom, lerpAlpha)
        }
        camera.updateProjectionMatrix()
      }
    }

    const controls = controlsRef.current
    if (controls && cameraPresentation?.target) {
      if (shouldSnap) {
        controls.target.copy(cameraPresentation.target)
      } else {
        controls.target.lerp(cameraPresentation.target, lerpAlpha)
      }
      controls.update()
    }

    if (shouldSnap) {
      initialSnapFramesRef.current -= 1
    }
    if (isRestartTransition) {
      restartTransitionFramesRef.current -= 1
    }
  })
}
